import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Textarea,
  compressImage,
} from '@structural-climbing/ui'
import { type Discipline, gradesForDiscipline } from '@structural-climbing/validation'
import { ROUTE_COLORS, ROUTE_SHEET_PROPS } from './constants'
import { HoldColorPicker } from './HoldColorPicker'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import type { ClimbingRoute, GymSection } from './types'
import { useRouteSetters } from './useRouteSetters'

/**
 * What the form reads off a route. Narrower than ClimbingRoute so a competition
 * route, which has no grade and none of the ratings, can be edited here too.
 */
export type EditableRoute = Pick<ClimbingRoute, 'id' | 'color' | 'image_r2_key' | 'setter_note'> & {
  grade: number | null
  section: { id: string } | null
  /** Optional while a deploy is mid-flight and the Worker does not send them yet. */
  set_by?: string | null
  setter?: { name: string | null } | null
}

interface EditRouteModalProps {
  route: EditableRoute | null
  onClose: () => void
  onSaved: () => void
  apiUrl: string
  /** Sections for the active discipline tab (already filtered). */
  sections: GymSection[]
  /** Active discipline tab; fixes the grade scale and whether a section is required. */
  discipline?: Discipline
  /**
   * False for a route set for a competition: it is numbered there, not graded,
   * so the grade is neither asked for nor sent. Same rule as AddRouteModal.
   */
  graded?: boolean
}

export function EditRouteModal({
  route,
  onClose,
  onSaved,
  apiUrl,
  sections,
  discipline = 'boulder',
  graded = true,
}: EditRouteModalProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const gradeConfig = useGymGradeConfig()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [grade, setGrade] = useState<number | null>(null)
  const [color, setColor] = useState<string>(ROUTE_COLORS[0].value)
  const [sectionId, setSectionId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [setterId, setSetterId] = useState<string | null>(null)
  const team = useRouteSetters(apiUrl, !!route)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (route) {
      setGrade(route.grade)
      setColor(route.color ?? ROUTE_COLORS[0].value)
      setSectionId(route.section?.id ?? null)
      setNote(route.setter_note ?? '')
      setSetterId(route.set_by ?? null)
      setFile(null)
      setPreview(route.image_r2_key ? `${apiUrl}/climbing-routes/${route.id}/image` : null)
      setError(null)
    }
  }, [route, apiUrl])

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null
    setFile(selected)
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview)
    setPreview(selected ? URL.createObjectURL(selected) : null)
  }

  function handleClose() {
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview)
    onClose()
  }

  async function handleSubmit() {
    if (graded && !grade) {
      setError(t('routes.gradeRequired'))
      return
    }
    // A lead route must live in a rope section (a no-section route is boulder).
    if (discipline === 'lead' && !sectionId) {
      setError(t('routes.sectionRequired'))
      return
    }
    if (!route) return

    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fd = new FormData()
      if (graded) fd.append('grade', String(grade))
      fd.append('color', color)
      fd.append('section_id', sectionId ?? '')
      fd.append('setter_note', note.trim())
      // Only when switched, so an ordinary save never touches who set the route.
      if (setterId && setterId !== (route.set_by ?? null)) fd.append('set_by', setterId)
      if (file) {
        const compressed = await compressImage(file, 2 * 1024 * 1024, 2000)
        fd.append('file', compressed)
        const thumbnail = await compressImage(file, 50 * 1024, 200)
        fd.append('thumbnail', thumbnail)
      }

      const res = await fetch(`${apiUrl}/climbing-routes/${route.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const json = (await res.json()) as { error?: string }
        throw new Error(json.error ?? t('routes.errorUpdate'))
      }
      onSaved()
      handleClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routes.errorUpdate'))
    } finally {
      setLoading(false)
    }
  }

  // The grade scale is fixed by the active discipline tab; every section shown
  // here belongs to that discipline.
  const gradeOptions = gradesForDiscipline(gradeConfig, discipline)

  // Someone who set this route and has since left the team stays in the list,
  // so opening the form never shows the route as set by nobody.
  const currentSetter =
    route?.set_by && route.setter?.name && !team.some((s) => s.id === route.set_by)
      ? [{ id: route.set_by, name: route.setter.name }]
      : []
  const setterOptions = [...team, ...currentSetter]

  return (
    <Modal
      isOpen={!!route}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
      {...ROUTE_SHEET_PROPS}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('routes.editRouteTitle')}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <HoldColorPicker value={color} onChange={setColor} />

                {graded && (
                  <Select
                    label={t('routes.grade')}
                    placeholder={t('routes.gradePlaceholder')}
                    selectedKeys={grade ? [String(grade)] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0]
                      setGrade(val ? parseInt(String(val), 10) : null)
                    }}
                  >
                    {gradeOptions.map((g) => (
                      <SelectItem key={String(g.rank)} textValue={g.label}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {sections.length > 0 && (
                  <Select
                    label={t('routes.section')}
                    placeholder={
                      discipline === 'lead' ? t('routes.selectSection') : t('routes.noSection')
                    }
                    isRequired={discipline === 'lead'}
                    selectedKeys={sectionId ? [sectionId] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0]
                      setSectionId(val ? String(val) : null)
                    }}
                  >
                    {sections.map((s) => (
                      <SelectItem key={s.id}>{s.name}</SelectItem>
                    ))}
                  </Select>
                )}

                {team.length > 0 && (
                  <Select
                    label={t('routes.setter')}
                    disallowEmptySelection
                    selectedKeys={setterId ? [setterId] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0]
                      if (val) setSetterId(String(val))
                    }}
                  >
                    {setterOptions.map((s) => (
                      <SelectItem key={s.id} textValue={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                <Textarea
                  label={t('routes.setterNote')}
                  placeholder={t('routes.setterNotePlaceholder')}
                  value={note}
                  onValueChange={setNote}
                  maxLength={500}
                />

                <div>
                  <p className="mb-1.5 text-sm font-medium">{t('routes.photoLabel')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="preview"
                        className="h-48 w-full rounded-lg object-cover"
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        className="absolute right-2 top-2"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {t('routes.changePhoto')}
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-divider py-8 text-sm text-foreground/50 hover:bg-default-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{t('routes.addPhoto')}</span>
                      <span className="text-xs text-foreground/30">{t('routes.photoHint')}</span>
                    </button>
                  )}
                </div>

                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                className="flex-1 sm:flex-none"
                onClick={handleClose}
                isDisabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                className="flex-1 sm:flex-none"
                onClick={handleSubmit}
                isLoading={loading}
              >
                {t('routes.save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
